import { z } from 'zod';
import { PermissionKeySchema } from '../permissions/permissions.js';
import { AuthTenantDtoSchema } from '../tenants/tenants.schema.js';
import { PasswordSchema, UserRoleSchema } from '../users/users.schema.js';
/**
 * Logged-in user profile embedded in session and auth responses.
 */
export const AuthUserDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    email: z.email('Niepoprawny format adresu e-mail').max(320),
    displayName: z.string().min(1).max(255),
    role: UserRoleSchema,
    hasFinanceAccess: z.boolean(),
    twoFactorEnabled: z.boolean(),
});
/** Login request (email + password). */
export const LoginRequestSchema = z.object({
    email: z.email('Niepoprawny format adresu e-mail').max(320),
    password: z.string().min(1, 'Hasło jest wymagane').max(128),
});
/** Full success response when 2FA is already satisfied or not required. */
export const LoginSuccessResponseSchema = z.object({
    status: z.literal('authenticated'),
    accessToken: z.string().min(1),
    user: AuthUserDtoSchema,
    tenant: AuthTenantDtoSchema,
});
/** Challenge response when 2FA TOTP code is required to complete authentication. */
export const LoginMfaRequiredResponseSchema = z.object({
    status: z.literal('mfa_required'),
    mfaTicket: z.string().min(1),
    expiresAt: z.iso.datetime(),
});
/** Discriminated union response for login. */
export const LoginResponseSchema = z.discriminatedUnion('status', [
    LoginSuccessResponseSchema,
    LoginMfaRequiredResponseSchema,
]);
/** Submit 6-digit TOTP code during 2FA challenge. */
export const VerifyMfaRequestSchema = z.object({
    mfaTicket: z.string().min(1, 'Bilet MFA jest wymagany'),
    code: z.string().regex(/^\d{6}$/, 'Kod 2FA musi składać się z dokładnie 6 cyfr'),
    /**
     * Opt-in "remember this workstation" (2FA variant C — trusted workstation, D1).
     * Trust is granted ONLY after a genuine TOTP code (backup codes never grant it,
     * D6) and materialises as the httpOnly `ctms_trusted_device` cookie — the raw
     * token never appears in a response body.
     */
    rememberDevice: z.boolean().optional(),
});
/**
 * Name of the httpOnly cookie carrying the raw trusted-device token (D1).
 * The token crosses the HTTP boundary exactly once — in the Set-Cookie header
 * of POST /auth/mfa/verify — and is persisted only as a SHA-256 hash
 * (`auth_trusted_devices.token_hash`). Shared wire contract between api
 * (set/read) and web E2E (scenario setup).
 */
export const TRUSTED_DEVICE_COOKIE_NAME = 'ctms_trusted_device';
/** Response when initializing 2FA setup for user account. */
export const SetupTwoFactorResponseSchema = z.object({
    secret: z.string().min(1),
    totpUri: z.string().min(1),
    backupCodes: z.array(z.string().min(6)).optional(),
});
/** Finalize 2FA activation by providing the first valid 6-digit code. */
export const EnableTwoFactorRequestSchema = z.object({
    code: z.string().regex(/^\d{6}$/, 'Kod 2FA musi składać się z dokładnie 6 cyfr'),
});
/** Administrator reset of 2FA for a site staff member (REQ-CORE-014). */
export const ResetTwoFactorRequestSchema = z.object({
    userId: z.number().int().positive('Wymagany poprawny identyfikator użytkownika'),
});
/** Session verification endpoint response (/api/v1/auth/me). */
export const SessionResponseSchema = z.object({
    user: AuthUserDtoSchema,
    tenant: AuthTenantDtoSchema,
    permissions: z.array(PermissionKeySchema),
});
/** Password change request (REQ-CORE-015, REQ-CORE-017). */
export const ChangePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1, 'Bieżące hasło jest wymagane'),
    newPassword: PasswordSchema,
});
//# sourceMappingURL=auth.schema.js.map